// components/dashboard/DivisionDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  ArrowLeft, TrendingUp, DollarSign, BarChart3, 
  Calculator
} from 'lucide-react';
import api from '../../common/apiClient';
import DashboardCard from './DashboardCard';
import SummaryApi from '../../common/SummaryApi';

const DivisionDashboard = () => {
  const { divisionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [divisionName, setDivisionName] = useState('');

  const COLORS = ['#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#FBBF24', '#10B981', '#3B82F6', '#8B5CF6'];

  const fetchDivisionData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(SummaryApi.getDivisionDashboard.url(divisionId));
      setData(response.data.data);
      // Get division name from the data
      if (response.data.data.pipeline?.divisionName) {
        setDivisionName(response.data.data.pipeline.divisionName);
      }
    } catch (error) {
      console.error('Failed to fetch division dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [divisionId]);

  useEffect(() => {
    fetchDivisionData();
  }, [fetchDivisionData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const { summary, salesPersons, monthlyTrends, industries, pipeline, productCategories } = data || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header with Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{divisionName || 'Division'} Dashboard</h1>
            <p className="text-gray-600">Performance metrics and insights</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DashboardCard
          title="Total Deals"
          value={summary?.totalDeals || 0}
          icon={<BarChart3 className="h-6 w-6" />}
          color="red"
        />
        <DashboardCard
          title="Pipeline Value"
          value={`₹${(summary?.totalValue || 0).toLocaleString()}`}
          icon={<DollarSign className="h-6 w-6" />}
          color="red"
        />
        <DashboardCard
          title="Active Deals"
          value={summary?.activeDeals || 0}
          icon={<TrendingUp className="h-6 w-6" />}
          color="red"
        />
        <DashboardCard
          title="Avg Deal Size"
          value={`₹${(summary?.avgDealSize || 0).toLocaleString()}`}
          icon={<Calculator className="h-6 w-6" />}
          color="red"
        />
      </div>

      {/* Monthly Trends */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Monthly Performance Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="totalValue" 
              stroke="#DC2626" 
              name="Total Value"
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="totalDeals" 
              stroke="#3B82F6" 
              name="Deals Count"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Team Performance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Sales Team Performance</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={salesPersons?.slice(0, 5)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="userName" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
              <Bar dataKey="totalValue" fill="#DC2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Industry Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Industry Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={industries}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="totalValue"
                nameKey="industry"
              >
                {industries?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stage Pipeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Sales Pipeline</h2>
        <div className="flex items-center overflow-x-auto pb-2">
          {pipeline?.stages?.map((stage, index) => (
            <React.Fragment key={stage.stageId}>
              <div className="flex-shrink-0 min-w-[180px]">
                <div 
                  className="p-4 rounded-lg text-center"
                  style={{ backgroundColor: `${stage.color}15` }}
                >
                  <div 
                    className="w-3 h-3 rounded-full mx-auto mb-2"
                    style={{ backgroundColor: stage.color }}
                  />
                  <p className="font-medium text-sm">{stage.stageName}</p>
                  <p className="text-3xl font-bold mt-2">{stage.count}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    ₹{(stage.totalValue / 100000).toFixed(1)}L
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Avg: {stage.avgAge} days
                  </p>
                </div>
              </div>
              {index < pipeline.stages.length - 1 && (
                <div className="mx-2 text-gray-400">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Product Categories */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Top Product Categories</h2>
        <div className="space-y-3">
          {productCategories?.slice(0, 5).map((category, index) => (
            <div key={category.category} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="font-medium">{category.category}</span>
              </div>
              <div className="text-right">
                <p className="font-semibold">₹{category.totalValue.toLocaleString()}</p>
                <p className="text-sm text-gray-600">{category.deals} deals</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DivisionDashboard;
//http://localhost:3000/cuso-biz-vickysakthi8834/reports/division/6903436b247c3ba9a076657e